import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useLanguage, useSeo } from '../i18n/LanguageContext';
import { IMAGES } from '../data/images';
import { ArrowButton, Eyebrow, MaskedLines, Reveal, EASE } from '../components/bits';
import PageHero from '../components/PageHero';
import BeforeAfter from '../components/BeforeAfter';
import FinalCTA from '../components/FinalCTA';

const BA_KEYS = { 'bin-cleaning': 'bins', 'window-cleaning': 'window', 'lawn-care': 'lawn', 'gutter-cleaning': 'gutter' };

function ServiceBlock({ svc, index }) {
  const { t } = useLanguage();
  const flip = index % 2 === 1;
  const baKey = BA_KEYS[svc.id];
  return (
    <section
      id={svc.id}
      data-testid={`service-section-${svc.id}`}
      className={`scroll-mt-24 py-16 md:py-24 ${flip ? 'noise-bg bg-cream' : 'bg-white'}`}
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 sm:px-12 lg:grid-cols-12 lg:gap-16 lg:px-20">
        <Reveal className={`lg:col-span-7 ${flip ? 'lg:order-2' : ''}`}>
          {baKey ? (
            <BeforeAfter beforeImage={IMAGES[`${baKey}Before`]} afterImage={IMAGES[svc.img]} alt={svc.alt} labels={t.services.beforeAfter} />
          ) : svc.id === 'seasonal-care' ? (
            <div className="grid grid-cols-2 gap-4" data-testid="seasonal-dual">
              {[
                { src: IMAGES[svc.img], cap: t.services.dual[0], alt: svc.alt },
                { src: IMAGES.seasonalWinter, cap: t.services.dual[1], alt: svc.altWinter },
              ].map((f) => (
                <figure key={f.cap} className="group relative m-0 overflow-hidden">
                  <img
                    src={f.src}
                    alt={f.alt}
                    loading="lazy"
                    className="aspect-[3/4] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <figcaption className="absolute bottom-3 left-3 bg-ink/70 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
                    {f.cap}
                  </figcaption>
                </figure>
              ))}
            </div>
          ) : (
            <div className="overflow-hidden">
              <motion.img
                src={IMAGES[svc.img]}
                alt={svc.alt}
                loading="lazy"
                initial={{ scale: 1.08 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 1.2, ease: EASE }}
                className="aspect-[16/10] w-full object-cover"
              />
            </div>
          )}
        </Reveal>
        <div className={`lg:col-span-5 ${flip ? 'lg:order-1' : ''}`}>
          <span className="font-mono text-xs font-semibold tracking-[0.3em] text-[#C6A15B]">
            0{index + 1}
          </span>
          <MaskedLines
            as="h2"
            lines={[svc.name]}
            inView
            delay={0.05}
            className="mt-4 font-display text-2xl font-extrabold uppercase leading-[1.08] tracking-tight text-ink sm:text-3xl lg:text-4xl"
          />
          <Reveal delay={0.15}>
            <p className="mt-5 text-base leading-relaxed text-neutral-600 sm:text-lg">{svc.desc}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-forest">
              {t.services.includes}
            </p>
            <ul className="mt-4 space-y-2.5">
              {svc.includes.map((inc) => (
                <li key={inc} className="flex items-start gap-3 text-sm text-neutral-700">
                  <Plus className="mt-0.5 h-4 w-4 shrink-0 text-[#C6A15B]" strokeWidth={2.5} aria-hidden="true" />
                  {inc}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.25}>
            <ArrowButton to={`/contact?service=${svc.id}`} variant="green" dataTestId={`service-quote-${svc.id}`} className="mt-9">
              {t.services.requestQuote}
            </ArrowButton>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function YardSeasonal() {
  const { t } = useLanguage();
  const y = t.yardSeasonal;
  const panels = [
    { tag: y.springTag, name: y.opening.name, desc: y.opening.desc, includes: y.opening.includes, img: IMAGES.yardOpening, alt: y.openingAlt, id: 'yard-opening' },
    { tag: y.fallTag, name: y.closing.name, desc: y.closing.desc, includes: y.closing.includes, img: IMAGES.yardClosing, alt: y.closingAlt, id: 'yard-closing' },
  ];
  return (
    <section id="yard-opening-closing" className="bg-ink py-20 md:py-32" data-testid="yard-seasonal">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <Eyebrow dark>{y.eyebrow}</Eyebrow>
        <MaskedLines
          as="h2"
          lines={[y.heading]}
          inView
          delay={0.1}
          className="mt-6 font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl"
        />
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">{y.intro}</p>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-10">
          {panels.map((p) => (
            <Reveal key={p.id}>
              <article data-testid={p.id}>
                <div className="overflow-hidden">
                  <motion.img
                    src={p.img}
                    alt={p.alt}
                    loading="lazy"
                    initial={{ scale: 1.08 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 1.2, ease: EASE }}
                    className="aspect-[16/10] w-full object-cover"
                  />
                </div>
                <span className="mt-6 inline-block border border-[#C6A15B]/40 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-[#C6A15B]">
                  {p.tag}
                </span>
                <h3 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">
                  {p.name}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60 sm:text-base">{p.desc}</p>
                <ul className="mt-6 space-y-2.5">
                  {p.includes.map((inc) => (
                    <li key={inc} className="flex items-start gap-3 text-sm text-white/70">
                      <Plus className="mt-0.5 h-4 w-4 shrink-0 text-[#C6A15B]" strokeWidth={2.5} aria-hidden="true" />
                      {inc}
                    </li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-16 flex flex-col items-start gap-8 border-t border-white/15 pt-12 lg:flex-row lg:items-center lg:justify-between">
            <h3 className="max-w-xl font-display text-2xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-3xl">
              {y.ctaHeading}
            </h3>
            <ArrowButton to="/contact?service=yard-opening-closing" variant="gold" dataTestId="yard-seasonal-quote">
              {y.cta}
            </ArrowButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Services() {
  const { t } = useLanguage();
  useSeo('services');
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    }
  }, [hash]);

  return (
    <main>
      <PageHero
        eyebrow={t.services.eyebrow}
        lines={[t.services.heading]}
        sub={t.services.pageSub}
        img={IMAGES.servicesHero}
        alt={t.services.items[1].alt}
      />
      <div className="border-b border-ink/10 bg-white">
        <p className="mx-auto max-w-7xl px-6 py-5 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500 sm:px-12 lg:px-20">
          {t.services.scopeNote}
        </p>
      </div>
      {t.services.items.map((svc, i) => (
        <ServiceBlock key={svc.id} svc={svc} index={i} />
      ))}
      <YardSeasonal />
      <FinalCTA />
    </main>
  );
}
